// DiagiaCampaigns — list + create.
//
// Mirrors the "Campagnes récentes" table from the mockup. Each row links to
// the campaign detail page. A primary CTA opens the create modal; the same
// modal can be triggered from the Home page's "Créer une campagne" button
// via the `?new=1` query-string trick used below.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FaBullhorn,
  FaPlusCircle,
  FaCheckCircle,
  FaPauseCircle,
  FaArchive,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';
import CreateCampaignModal from '../components/CreateCampaignModal';

const STATUS_META = {
  active:   { label: 'Active',     icon: <FaCheckCircle />, className: 'is-active'   },
  draft:    { label: 'Brouillon',  icon: <FaPauseCircle />, className: 'is-draft'    },
  archived: { label: 'Archivée',   icon: <FaArchive />,     className: 'is-archived' },
};

const formatProgression = (p) => {
  if (p === null || p === undefined) return '—';
  return `${Math.round(p * 100)}%`;
};

export default function DiagiaCampaigns() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const [params, setParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(params.get('new') === '1');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await api.listCampaigns();
      setItems(rows);
    } catch (e) {
      setError(e?.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // If we landed with ?new=1, drop the query so a refresh doesn't reopen.
  useEffect(() => {
    if (params.get('new') === '1') {
      const next = new URLSearchParams(params);
      next.delete('new');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (payload) => {
    await api.createCampaign(payload);
    await load();
  };

  return (
    <div className="dia-page">
      <header className="dia-page-head">
        <div>
          <span className="dia-section-eyebrow">{t('diagia.nav.admin_section', 'Administration')}</span>
          <h1>{t('diagia.nav.campaigns', 'Campagnes')}</h1>
          <p>{t('diagia.camp.sub', 'Groupez vos diagnostics par thématique ou par direction.')}</p>
        </div>
        <button
          type="button"
          className="dia-cta dia-cta-primary"
          onClick={() => setModalOpen(true)}
        >
          <FaPlusCircle /> Créer une campagne
        </button>
      </header>

      {loading && <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>}
      {!loading && error && <p className="dia-error">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="dia-empty-card">
          <FaBullhorn size={28} />
          <h3>{t('diagia.camp.empty_title', "Aucune campagne pour l'instant")}</h3>
          <p>{t('diagia.camp.empty_body', "Lancez une campagne pour inviter votre équipe à identifier des cas d'usage.")}</p>
          <button
            type="button"
            className="dia-cta dia-cta-primary"
            onClick={() => setModalOpen(true)}
          >
            <FaPlusCircle /> Créer une campagne
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="dia-card">
          <table className="dia-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>{t('diagia.home.th_scope', 'Périmètre')}</th>
                <th>{t('diagia.nav.participants', 'Participants')}</th>
                <th>{t('diagia.camp.th_progress', 'Progression')}</th>
                <th>{t('diagia.home.th_status', 'Statut')}</th>
                <th>{t('diagia.home.th_created_f', 'Créée le')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const status = STATUS_META[c.status] || STATUS_META.active;
                const progressPct = c.progression !== null && c.progression !== undefined
                  ? Math.min(100, Math.round(c.progression * 100))
                  : 0;
                return (
                  <tr key={c.id}>
                    <td className="dia-table-title">
                      <Link to={`/diagia/campaigns/${c.id}`}>{c.name}</Link>
                    </td>
                    <td>{c.scope || <span className="dia-table-muted">—</span>}</td>
                    <td>{c.participant_count ?? 0}</td>
                    <td>
                      <div className="dia-progress-cell">
                        <div className="dia-progress-track">
                          <span style={{ width: `${progressPct}%` }} />
                        </div>
                        <span className="dia-progress-pct">{formatProgression(c.progression)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`dia-status ${status.className}`}>
                        {status.icon} {status.label}
                      </span>
                    </td>
                    <td>{new Date(c.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateCampaignModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  );
}
